import { useEffect, useState } from 'react';
import type { Profile } from '@etiquetachef/types';
import { subscribeProfile } from '@/lib/firestoreProfiles';

/**
 * Assina os perfis (`/profiles/{uid}`) de uma lista de uids e devolve um mapa
 * `uid → Profile`. Usado pelo painel para preencher o telefone do OWNER, cujo
 * número verificado por OTP fica no perfil global e não no doc `member`.
 *
 * A dependência do efeito é a lista de uids serializada (`join`) — assim o
 * efeito só re-assina quando o conjunto realmente muda, não a cada render.
 *
 * Origem: sem issue — commit 10949c80e, 2026-06-27 14:51 +0200 (contato do
 * owner no detalhe do tenant).
 *
 * Contrato: um listener por uid (N leituras abertas — use com listas
 * pequenas: o owner, ou os membros de um tenant). Perfil apagado sai do mapa;
 * perfil ainda não lido simplesmente não está nele.
 *
 * Usado por: `features/tenants/TenantInfoCard.tsx` (contato do owner) e
 * `features/members/MembersTab.tsx` (telefone como fallback do member).
 *
 * Dados: `profiles/{uid}` validado por `ProfileSchema` em
 * `lib/firestoreProfiles.ts`. A rule de `profiles` libera leitura a
 * super-admin desde o mesmo commit de origem.
 *
 * Dicas: a ordem dos uids entra na chave — a mesma lista em outra ordem
 * re-assina tudo.
 */
export function useProfiles(uids: readonly string[]): Record<string, Profile> {
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const key = uids.join(',');

  useEffect(() => {
    if (uids.length === 0) {
      setProfiles({});
      return;
    }
    const unsubs = uids.map((uid) =>
      subscribeProfile(uid, (profile) => {
        setProfiles((prev) => {
          if (profile === null) {
            if (!(uid in prev)) return prev;
            return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== uid));
          }
          return { ...prev, [uid]: profile };
        });
      }),
    );
    return (): void => {
      unsubs.forEach((unsub) => {
        unsub();
      });
    };
    // `key` resume a lista de uids; `uids` em si muda de referência a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return profiles;
}
